import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FAQAccordion } from '../components/faq/FAQAccordion';
export function FAQ() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: 'How long does it take to build a website?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For standard websites, we typically deliver within 2-3 weeks from the date we receive all required content and approvals. E-commerce websites generally take 3-5 weeks depending on complexity. We provide you with a specific timeline during our initial consultation, based on your project requirements.'
      }
    }, {
      '@type': 'Question',
      name: 'Who owns the website once it is completed?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You do! Once the project is paid in full, you own 100% of your website. We never hold your site hostage or restrict access. We provide all necessary login credentials and can assist with transferring the site if needed in the future.'
      }
    }, {
      '@type': 'Question',
      name: 'Do you provide hosting services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, all our websites come with 3 months of free hosting. After that, you can choose one of our Website Care Plans which include hosting, or we can help you set up hosting elsewhere if you prefer.'
      }
    }, {
      '@type': 'Question',
      name: 'Do you write content for websites?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes, we offer content writing services for an additional fee. Our Silver and Bronze packages don't include content creation by default, but our Gold package includes basic content writing. We can also work with content you provide and optimize it for SEO."
      }
    }, {
      '@type': 'Question',
      name: 'What support do you provide after launch?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'All our packages include at least 30 days of post-launch support to address any issues that might arise. For ongoing support, we recommend our Website Care Plans, which include regular updates, security monitoring, and technical support.'
      }
    }]
  };
  return <>
      <Helmet data-id="element-1535">
        <title data-id="element-1536">Web Design FAQs | Timelines, Ownership, Hosting & SEO</title>
        <meta name="description" content="Answers to common questions about web design, hosting, SEO, content and support." data-id="element-1537" />
        <script type="application/ld+json" data-id="element-1538">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1539">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1540">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1541">
            <motion.span initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1542">
              FAQ
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6" data-id="element-1543">
              Frequently Asked Questions
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8" data-id="element-1544">
              Find answers to the most common questions about our web design
              services, hosting, SEO, and support.
            </motion.p>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-1545">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1546">
          <div className="max-w-3xl mx-auto" data-id="element-1547">
            <FAQAccordion data-id="element-1548" />
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1549">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1550">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1551">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1552">
              Still have questions?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1553">
              If you couldn't find the answer to your question, please don't
              hesitate to contact us directly. We're here to help!
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1554">
              <a href="/contact" className="bg-[#3AA6F9] text-white hover:bg-[#2A95E8] px-6 py-3 rounded-md font-medium transition-colors" data-id="element-1555">
                Contact Us
              </a>
              <a href="mailto:info@bright-pixxel.com" className="border-2 border-[#3AA6F9] text-[#3AA6F9] hover:bg-[#3AA6F9] hover:text-white px-6 py-3 rounded-md font-medium transition-colors" data-id="element-1556">
                Email Us
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}